import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Eye, Star, Award } from "lucide-react";
import plantFoodSticks from "../../assets/products/block2.png";
import gardenSoilMix from "../../assets/products/25g.png";
import kgbricks from "../../assets/products/block1.png";
import growrich from "../../assets/products/650gmss.png";
import bulkload from "../../assets/cocImg/cocimg2.jpeg";
import cocoDisc from "../../assets/products/coins12.png";
import { Helmet } from "react-helmet";

const products = [
  { 
    name: "Cocopeat 650GM", 
    price: "₹ 120", 
    originalPrice: "₹ 179", 
    imgSrc: growrich, 
    path: "/product/cocopeat-650gm",
    badge: "Best Seller",
    rating: 4.8,
    reviews: 124,
    description: "Perfect for indoor plants and small gardens"
  },
  { 
    name: "Coco peat 1kg bricks", 
    price: "₹ 229", 
    originalPrice: "₹ 330", 
    imgSrc: kgbricks, 
    path: "/product/1kg-bricks",
    badge: "Popular",
    rating: 4.7,
    reviews: 89,
    description: "Ideal for medium-sized gardening projects"
  },
  { 
    name: "Coco peat 5kg bricks", 
    price: "₹ 349", 
    originalPrice: "₹ 399", 
    imgSrc: plantFoodSticks, 
    path: "/product/5kg-bricks",
    badge: "Value Pack",
    rating: 4.9,
    reviews: 156,
    description: "Great for large gardens and landscaping"
  },
  { 
    name: "25kg Bags", 
    price: "₹ 450", 
    originalPrice: "₹ 539", 
    imgSrc: gardenSoilMix, 
    path: "/product/25kg-bricks",
    badge: "Bulk Save",
    rating: 4.6,
    reviews: 67,
    description: "Professional grade for commercial use"
  },
  { 
    name: "Cocopeat Coins", 
    price: "₹ 450", 
    originalPrice: "₹ 539", 
    imgSrc: cocoDisc, 
    path: "/product/cocopeat-coins",
    badge: "Easy Use",
    rating: 4.8,
    reviews: 92,
    description: "Convenient for seed starting and propagation"
  },
  { 
    name: "Bulk Loads", 
    price: "₹ 700", 
    originalPrice: "₹ 859", 
    imgSrc: bulkload, 
    path: "/product/bulk-loads",
    badge: "Wholesale",
    rating: 4.5,
    reviews: 43,
    description: "Custom quantities for farms and nurseries"
  },
];

const ProductList = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);


  const getBadgeColor = (badge) => {
    switch(badge) {
      case "Best Seller": return "bg-red-500";
      case "Popular": return "bg-blue-500";
      case "Value Pack": return "bg-purple-500";
      case "Bulk Save": return "bg-orange-500";
      case "Easy Use": return "bg-green-500";
      case "Wholesale": return "bg-indigo-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white min-h-screen pt-12 md:pt-6">
      <Helmet>
        <title>Cocopeat Product Range | Best Cocopeat Exporters in Andhra Pradesh, Anantapur</title>
        <meta name="description" content="Explore the full range of cocopeat products from Tirumala Cocopeat, the best exporters and suppliers in Andhra Pradesh, Anantapur. Find cocopeat bricks, blocks, discs, and bulk loads for agriculture, horticulture, and export." />
        <meta name="keywords" content="Tirumala Cocopeat, cocopeat products, cocopeat exporters, best cocopeat in Andhra Pradesh, best cocopeat in Anantapur, cocopeat suppliers, cocopeat manufacturers, cocopeat India, cocopeat for export, cocopeat exporters, exports, premium cocopeat, organic cocopeat, cocopeat business, cocopeat company, cocopeat bricks, cocopeat blocks, cocopeat discs, bulk cocopeat, cocopeat for agriculture, cocopeat for horticulture, cocopeat for hydroponics, cocopeat for farming, cocopeat for nurseries, cocopeat for greenhouses" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Cocopeat Product Range | Best Cocopeat Exporters in Andhra Pradesh, Anantapur" />
        <meta property="og:description" content="Explore the full range of cocopeat products from Tirumala Cocopeat, the best exporters and suppliers in Andhra Pradesh, Anantapur. Find cocopeat bricks, blocks, discs, and bulk loads for agriculture, horticulture, and export." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tirumalacocopeat.vercel.app/products" />
      </Helmet>
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Award className="w-4 h-4" />
            Premium Quality Products
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our Premium Product Range
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our carefully curated collection of high-quality cocopeat and coir products, 
            designed to meet all your gardening and agricultural needs.
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-600 mb-1">500+</div>
            <div className="text-sm text-gray-600">Happy Customers</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-600 mb-1">6</div>
            <div className="text-sm text-gray-600">Premium Products</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-600 mb-1">4.7★</div>
            <div className="text-sm text-gray-600">Average Rating</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-600 mb-1">24/7</div>
            <div className="text-sm text-gray-600">Support</div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
          {products.map((product, index) => (
            <div
              key={product.name}
              className="group bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col h-full"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              {/* Product Image Container */}
              <div className="relative aspect-square bg-gray-50 overflow-hidden">
                {/* Badge */}
                <div className="absolute top-3 left-3 z-10">
                  <span className={`${getBadgeColor(product.badge)} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md`}>
                    {product.badge}
                  </span>
                </div>
                
                {/* Discount Badge */}
                {/* <div className="absolute top-3 right-3 z-10">
                  <div className="bg-red-500 text-white px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {calculateDiscount(product.originalPrice, product.price)}% OFF
                  </div>
                </div> */}

                {/* Product Image */}
                <img
                  src={product.imgSrc}
                  alt={`${product.name} - Premium quality cocopeat product`}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  {/* <div className="text-white text-center">
                    <Eye className="w-8 h-8 mx-auto mb-2" />
                    <span className="text-sm font-medium">Quick View</span>
                  </div> */}
                </div>
              </div>

              {/* Product Details */}
              <div className="p-5 flex flex-col flex-1">
                <div className="flex-1">
                  {/* Product Name */}
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors line-clamp-2">
                    {product.name}
                  </h3>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(product.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {product.rating} ({product.reviews})
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Price */}
                  <div className="mb-4">
                    {/* <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl md:text-2xl font-bold text-green-600">
                        {product.price}
                      </span>
                      <span className="text-sm text-gray-400 line-through">
                        {product.originalPrice}
                      </span>
                    </div> */}
                    {/* <div className="text-xs text-gray-500">
                      You save ₹{parseInt(product.originalPrice.slice(2)) - parseInt(product.price.slice(2))}
                    </div> */}
                  </div>
                </div>

                {/* Action Button */}
                <Link 
                  to={product.path}
                  className="w-full group/button"
                  aria-label={`View details for ${product.name}`}
                >
                  <button className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group-hover/button:from-green-700 group-hover/button:to-green-800">
                    <Eye className="w-5 h-5 group-hover/button:animate-pulse" />
                    <span>View Product</span>
                  </button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 md:p-12 text-white text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Looking for Custom Solutions?
            </h3>
            <p className="text-lg mb-6 text-green-100">
              We offer customized cocopeat solutions for large-scale projects, 
              commercial farms, and specialized gardening needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center">
              Get Custom Quote
            </Link>
            <Link to="/contact" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors text-center">
              Contact Expert
            </Link>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;